import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { setApiAuthToken, authApi } from '../services/api';

const AuthContext = createContext(null);

// ── Session storage key for persisting auth state ──
const AUTH_STORAGE_KEY = 'trustcenter_auth';

// ── Allowed parent origins that may post tokens via postMessage ──
const TRUSTED_ORIGINS = [
  'https://localhost:5001',  // Blazor dev server (common)
  'https://localhost:44381',
  'https://localhost:7109',
  'http://localhost:3000',   // React dev server (for testing postMessage locally)
  'https://itpn-riskassessment-staging.azurewebsites.net',
  'https://itpn-riskassessment.azurewebsites.net',
  'https://myriskshield.com',
  window.location.origin,
];

// ── Helper to save auth state to sessionStorage ──
const saveAuthToStorage = (authData) => {
  try {
    sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
    console.log('[AuthContext] Auth state saved to sessionStorage');
  } catch (e) {
    console.warn('[AuthContext] Failed to save to sessionStorage:', e);
  }
};

// ── Helper to load auth state from sessionStorage ──
const loadAuthFromStorage = () => {
  try {
    const stored = sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('[AuthContext] Restored auth from sessionStorage:', { 
        role: parsed.role, 
        userName: parsed.userName,
        organization: parsed.user?.organization || parsed.organization || '(not set)'
      });
      return parsed;
    }
  } catch (e) {
    console.warn('[AuthContext] Failed to load from sessionStorage:', e);
  }
  return null;
};

/**
 * AuthProvider — receives the Keycloak access token from the parent RiskWebApp
 * via postMessage. No separate login/register flow is needed; the user is
 * already authenticated through the Blazor host.
 */
export const AuthProvider = ({ children }) => {
  // ── Always wait for fresh auth from parent RiskWebApp via postMessage ──
  // Never restore from sessionStorage to avoid showing stale cached data
  const stored = null;

  const [user, setUser] = useState(stored?.user || null);
  const [token, setToken] = useState(stored?.token || null);
  const [role, setRole] = useState(stored?.role || null);
  const [assessmentId, setAssessmentId] = useState(stored?.assessmentId || null);
  const [features, setFeatures] = useState(stored?.features || []);
  const [loading, setLoading] = useState(true); // Always start loading since we wait for postMessage
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Not authenticated until we receive token
  const tokenRef = useRef(null);

  // No need to restore API token from storage since we always wait for postMessage

  // ── Listen for the RISKWEBAPP_AUTH message from the parent Blazor iframe host ──
  useEffect(() => {
    const handleMessage = (event) => {
      // Validate origin
      if (!TRUSTED_ORIGINS.includes(event.origin) && event.origin !== '*') {
        // In iframes the origin may be the parent host — allow if it's https
        if (!event.origin.startsWith('https://')) {
          console.warn('[AuthContext] Ignored message from untrusted origin:', event.origin);
          return;
        }
      }

      const data = event.data;
      if (data && data.type === 'RISKWEBAPP_AUTH' && data.payload) {
        const p = data.payload;
        console.log('[AuthContext] Received auth payload from RiskWebApp:', {
          role: p.role,
          userName: p.userName,
          organization: p.organization || '(not provided)',
          hasToken: !!p.accessToken,
        });

        // Use the role from Blazor directly - it already parsed Keycloak claims
        const resolvedRole = p.role || 'trustcenteruser';
        console.log('[AuthContext] Using role from Blazor payload:', resolvedRole);

        if (p.accessToken) {
          setToken(p.accessToken);
          tokenRef.current = p.accessToken;
          // Set the token for API calls
          setApiAuthToken(p.accessToken);
        }

        const enabledFeatures = Array.isArray(p.enabledFeatures) ? p.enabledFeatures : [];
        console.log('[AuthContext] Enabled features received from RiskWebApp:', enabledFeatures);

        const userData = {
          id: p.userId || '',
          name: p.userName || '',
          email: p.email || '',
          role: resolvedRole,
          assessmentId: p.assessmentId || null,
          roles: p.roles || [],
          organization: p.organization || '',
          department: p.department || '',
          discipline: p.discipline || p.focusArea || '',
          features: enabledFeatures,
        };

        // Set user info directly from payload (no API call needed for role)
        setRole(resolvedRole);
        setAssessmentId(p.assessmentId || null);
        setUser(userData);
        setFeatures(enabledFeatures);
        setIsAuthenticated(true);
        setLoading(false);

        // Save to sessionStorage for persistence across iframe reloads
        saveAuthToStorage({
          token: p.accessToken,
          role: resolvedRole,
          user: userData,
          userName: p.userName,
          organization: p.organization || '',
          features: enabledFeatures,
          assessmentId: p.assessmentId || null,
        });
      }
    };

    // Fallback function for URL param (also saves to sessionStorage)
    const fallbackToUrlParam = () => {
      const params = new URLSearchParams(window.location.search);
      const encoded = params.get('sid');
      let fallbackRole = 'trustcenteruser';
      if (encoded) {
        try {
          const decoded = atob(encoded);
          if (['admin', 'assessmentadmin', 'departmentadmin', 'department_admin', 'reviewer', 'user', 'trustcenteruser'].includes(decoded)) {
            fallbackRole = decoded;
            console.log('[AuthContext] Decoded role from URL sid:', fallbackRole);
          }
        } catch (_) { /* ignore */ }
      }
      
      const userData = { id: '', name: 'User', email: '', role: fallbackRole, roles: [fallbackRole] };
      setRole(fallbackRole);
      setUser(userData);
      setIsAuthenticated(true);
      setLoading(false);
    };

    window.addEventListener('message', handleMessage);

    // ── Fallback: if no postMessage arrives within 3 s, try URL ?sid= param ──
    const fallbackTimer = setTimeout(() => {
      if (!tokenRef.current) {
        console.log('[AuthContext] No postMessage received — falling back to URL role');
        fallbackToUrlParam();
      } else {
        console.log('[AuthContext] Token received via postMessage, no fallback needed');
      }
    }, 3000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(fallbackTimer);
    };
  }, []);

  // ── Logout just clears local state (actual session lives in RiskWebApp/Keycloak) ──
  const logout = useCallback(() => {
    setToken(null);
    tokenRef.current = null;
    setUser(null);
    setRole(null);
    setFeatures([]);
    setIsAuthenticated(false);
    
    // Clear session storage to prevent auth restoration on reload
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    console.log('[AuthContext] Session storage cleared on logout');
  }, []);

  // ── Feature flag helper: check if a feature (e.g. 'cud_dashboard') is enabled for the user ──
  const isFeatureEnabled = useCallback(
    (featureName) => Array.isArray(features) && features.includes(featureName),
    [features]
  );

  // ── Fetch wrapper that attaches the Keycloak Bearer token automatically ──
  const authFetch = useCallback(async (url, options = {}) => {
    const currentToken = tokenRef.current;
    const headers = {
      ...options.headers,
    };
    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    const response = await fetch(url, { ...options, headers });
    return response;
  }, []);

  const value = {
    user,
    token,
    role,
    assessmentId,
    features,
    isFeatureEnabled,
    loading,
    isAuthenticated,
    logout,
    authFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Provide safe defaults when AuthProvider is not mounted (fallback)
    return {
      user: null,
      token: null,
      role: null,
      features: [],
      isFeatureEnabled: () => false,
      loading: false,
      isAuthenticated: true,
      logout: async () => {},
      authFetch: async (url, options = {}) => fetch(url, options),
    };
  }
  return context;
};

export default AuthContext;
